import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import apiClient from '../services/apiClient'
import Layout from '../components/Layout'
import ExperimentCard from '../components/ExperimentCard'
import ExperimentModal from '../components/ExperimentModal'
import ExperimentViewModal from '../components/ExperimentViewModal'
import Spinner from '../components/Spinner'
import ConfirmModal from '../components/ConfirmModal'
import EmptyState from '../components/EmptyState'

const BriefIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 12h6M9 16h6M17 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/>
    <path d="M9 8h1"/>
  </svg>
)

export default function Experiments() {
  const { user, effectiveRole } = useAuth()
  const navigate  = useNavigate()
  const isAdmin   = effectiveRole === 'admin'

  const [experiments, setExperiments] = useState([])
  const [userSurveysMap, setUserSurveysMap] = useState({})
  const [teams, setTeams]             = useState([])
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)
  const [search, setSearch]           = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [teamFilter, setTeamFilter]   = useState(() => user?.team_id ?? '')

  // Modal state
  const [modalExperiment, setModalExperiment] = useState(null)
  const [modalOpen, setModalOpen]             = useState(false)

  // View AI Brief modal state
  const [viewBriefExperiment, setViewBriefExperiment] = useState(null)

  // Confirm modal state
  const [confirmState, setConfirmState] = useState(null)

  const load = () => {
    setLoading(true)
    setError(null)
    Promise.all([
      apiClient.get('/admin/experiments'),
      effectiveRole === 'viewer' ? Promise.resolve({ data: { items: [] } }) : apiClient.get('/surveys/mine'),
      apiClient.get('/admin/teams'),
    ])
      .then(([expRes, mineRes, teamsRes]) => {
        setExperiments(expRes.data.items)
        setTeams(teamsRes.data.items ?? [])
        const map = {}
        for (const entry of mineRes.data.items) {
          map[entry.experiment_id] = entry.survey_id
        }
        setUserSurveysMap(map)
      })
      .catch(() => setError('Failed to load AI Briefs.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = experiments
    if (statusFilter === 'active')   list = list.filter((e) => e.is_active)
    if (statusFilter === 'inactive') list = list.filter((e) => !e.is_active)
    if (teamFilter !== '') list = list.filter((e) => e.team_id === Number(teamFilter))
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((e) => e.experiment_name.toLowerCase().includes(q))
    }
    return list
  }, [experiments, statusFilter, teamFilter, search])

  const active   = filtered.filter((e) => e.is_active)
  const inactive = filtered.filter((e) => !e.is_active)

  const openCreate = () => { setModalExperiment(null); setModalOpen(true) }
  const openEdit   = (exp) => { setModalExperiment(exp); setModalOpen(true) }
  const closeModal = () => setModalOpen(false)
  const afterSave  = () => { closeModal(); load() }

  const handleTakeSurvey  = (exp) => {
    const existingSurveyId = userSurveysMap[exp.id]
    if (existingSurveyId) {
      navigate(`/survey?experiment_id=${exp.id}&edit=${existingSurveyId}`)
    } else {
      navigate(`/survey?experiment_id=${exp.id}`)
    }
  }
  const handleViewResults = (exp) => navigate(`/results?experiment_id=${exp.id}`)

  const handleToggleActive = (exp) => {
    const isDeactivate = exp.is_active
    setConfirmState({
      title:          isDeactivate ? 'Deactivate AI Brief' : 'Reactivate AI Brief',
      message:        isDeactivate
        ? `"${exp.experiment_name}" will no longer appear in the survey form.`
        : `"${exp.experiment_name}" will become available for new surveys.`,
      confirmLabel:   isDeactivate ? 'Deactivate' : 'Reactivate',
      confirmVariant: isDeactivate ? 'danger' : 'primary',
      onConfirm: async () => {
        setConfirmState(null)
        try {
          await apiClient.put(`/admin/experiments/${exp.id}`, { is_active: !exp.is_active })
          load()
        } catch (ex) {
          setError(ex.response?.data?.detail || `Failed to ${isDeactivate ? 'deactivate' : 'reactivate'}.`)
        }
      },
    })
  }

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title">AI Briefs</h1>
        {isAdmin && (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + New AI Brief
          </button>
        )}
      </div>

      {/* Search & filter bar */}
      <div className="filter-bar" style={{ marginBottom: '1.5rem' }}>
        <input
          type="search"
          className="search-input"
          placeholder="Search AI Briefs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search AI Briefs"
        />
        <div className="filter-group">
          <label htmlFor="status-filter">Status</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="team-filter">Team</label>
          <select
            id="team-filter"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
          >
            <option value="">All Teams</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="loading-state">
          <Spinner size="md" />
          Loading AI Briefs…
        </div>
      ) : (
        <>
          {active.length === 0 && inactive.length === 0 && (
            <EmptyState
              icon={<BriefIcon />}
              title={search ? 'No matches found' : 'No AI Briefs yet'}
              description={
                search
                  ? 'Try a different search term or clear the filter.'
                  : 'Create the first AI Brief to start collecting survey feedback.'
              }
              action={
                isAdmin && !search
                  ? <button type="button" className="btn btn-primary" onClick={openCreate}>+ New AI Brief</button>
                  : null
              }
            />
          )}

          {active.length > 0 && (
            <div className="experiment-grid">
              {active.map((exp) => (
                <ExperimentCard
                  key={exp.id}
                  experiment={exp}
                  userRole={effectiveRole}
                  userSurveyId={userSurveysMap[exp.id] ?? null}
                  onViewBrief={setViewBriefExperiment}
                  onTakeSurvey={handleTakeSurvey}
                  onViewResults={handleViewResults}
                  onEdit={openEdit}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </div>
          )}

          {inactive.length > 0 && (
            <>
              <div className="inactive-divider">Inactive</div>
              <div className="experiment-grid">
                {inactive.map((exp) => (
                  <ExperimentCard
                    key={exp.id}
                    experiment={exp}
                    userRole={effectiveRole}
                    userSurveyId={userSurveysMap[exp.id] ?? null}
                    onViewBrief={setViewBriefExperiment}
                    onTakeSurvey={handleTakeSurvey}
                    onViewResults={handleViewResults}
                    onEdit={openEdit}
                    onToggleActive={handleToggleActive}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {modalOpen && (
        <ExperimentModal
          experiment={modalExperiment}
          onSave={afterSave}
          onClose={closeModal}
        />
      )}

      {viewBriefExperiment && (
        <ExperimentViewModal
          experiment={viewBriefExperiment}
          onClose={() => setViewBriefExperiment(null)}
        />
      )}

      {confirmState && (
        <ConfirmModal
          isOpen
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          confirmVariant={confirmState.confirmVariant}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </Layout>
  )
}
